/**
 * Gates every purchase path of a professional product behind a confirmation dialog.
 *
 * Cart submissions are caught on `document` in the capture phase on purpose: FoxTheme's
 * `ProductForm` binds its handler on the form element itself, so stopping the event on the
 * way down is the only way to get in front of it without patching `theme.js` — which a theme
 * update would overwrite. It also covers both add-to-cart forms a product page can render:
 * the main one and the separate one the sticky bar builds for itself.
 *
 * Express checkout needs a second mechanism. It never fires a submit event, so `buy-buttons`
 * stands a shield in its place that opens this dialog instead, and confirming reveals the real
 * button rather than buying: wallets only open their payment sheet on a genuine user gesture.
 */
class ProfessionalProductDisclaimer extends HTMLElement {
  connectedCallback() {
    this.sectionElement = this.closest('.shopify-section');
    this.pendingForm = null;
    this.pendingSubmitter = null;
    this.pendingGate = null;
    this.isConfirmed = false;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    document.addEventListener('submit', this.handleSubmit, { capture: true, signal });
    this.consentElement?.addEventListener('change', this.handleConsentChange, { signal });
    this.confirmElement?.addEventListener('click', this.handleConfirm, { signal });
    // Delegated: product-info swaps parts of its markup on variant change, so the shield the
    // shopper clicks is not necessarily the one that was here when this element connected.
    this.sectionElement?.addEventListener('click', this.handleShieldClick, { signal });
    // The modal reports its own close, no matter whether it came from cancel, overlay or Escape.
    this.modalElement?.addEventListener('modal:handleAfterHide', this.reset, { signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  // Resolved by id rather than by descendant lookup: the modal moves itself to <body> while open.
  get modalElement() {
    return (this._modalElement ||= document.getElementById(this.dataset.modalId));
  }

  get consentElement() {
    return this.modalElement?.querySelector('[data-professional-disclaimer-consent]');
  }

  get confirmElement() {
    return this.modalElement?.querySelector('[data-professional-disclaimer-confirm]');
  }

  handleSubmit = (event) => {
    if (this.isConfirmed) return;

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.dataset.type !== 'add-to-cart-form') return;
    // Strict on purpose: without a section to scope to, this would gate every other product's
    // form on the page — the quick view carries one of its own.
    if (!this.sectionElement?.contains(form)) return;

    event.preventDefault();
    event.stopPropagation();

    this.pendingForm = form;
    this.pendingSubmitter = event.submitter;
    this.modalElement?.show(event.submitter);
  };

  handleShieldClick = (event) => {
    const shield = event.target.closest('[data-professional-disclaimer-shield]');
    if (!shield) return;

    this.pendingGate = shield.closest('[data-professional-disclaimer-gate]');
    this.modalElement?.show(shield);
  };

  handleConsentChange = () => {
    if (this.confirmElement) this.confirmElement.disabled = !this.consentElement?.checked;
  };

  handleConfirm = async () => {
    const form = this.pendingForm;
    const submitter = this.pendingSubmitter;
    const gate = this.pendingGate;
    if (!form && !gate) return;

    // Closing is async, so without this a double click would reach the cart twice.
    if (this.confirmElement) this.confirmElement.disabled = true;

    // Close first so neither the cart drawer nor a payment sheet opens behind the dialog.
    await this.modalElement?.hide();

    if (gate) {
      this.unlockGate(gate);
      return;
    }

    // Replaying through requestSubmit keeps FoxTheme's own handler in charge of the request,
    // including the submitter it uses to restore focus and to open the cart drawer.
    this.isConfirmed = true;
    if (submitter && submitter.form === form) {
      form.requestSubmit(submitter);
    } else {
      form.requestSubmit();
    }
    this.isConfirmed = false;
  };

  unlockGate(gate) {
    const content = gate.querySelector('[data-professional-disclaimer-gated]');
    content?.removeAttribute('inert');
    gate.querySelector('[data-professional-disclaimer-shield]')?.remove();
    gate.dataset.professionalDisclaimerGate = 'unlocked';
    // The shield held the focus and is gone now, so hand it to the button it stood in for.
    setTimeout(() => content?.focus());
  }

  // Every cart attempt has to be confirmed again, so the dialog always reopens in its initial state.
  reset = () => {
    this.pendingForm = null;
    this.pendingSubmitter = null;
    this.pendingGate = null;
    if (this.consentElement) this.consentElement.checked = false;
    if (this.confirmElement) this.confirmElement.disabled = true;
  };
}

customElements.define('professional-product-disclaimer', ProfessionalProductDisclaimer);
